import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import config from '@/config'
import JSONBig from 'json-bigint';
import {
  handleUnpaidFeePrompt,
  clearUnpaidFeeState,
  isFeeGateSuccessUrl,
} from '@/utils/unpaidFeeDialog'
import { handleNoticeSignPrompt } from '@/utils/noticeSignPrompt'

const JSONparser = JSONBig({
  storeAsString: true, // 超出安全范围的整数会被转为字符串
  strict: true
});

// 暴露到 window 供其他模块轮询检测欠费弹框状态（由 unpaidFeeDialog 模块维护）
window.__isShowing1003Dialog = false
// 不显示登录过期提示的路径
const writePath = ['/course/list', '/course/video', '/course/audio', '/course/graphic', '/course/live', '/course/subject', '/course/topic', '/login', '/register', '/forgot-password', '/agreement-liveness', '/login-realperson']

/** 未结业（1005）：弹框提示，勿用红色 Toast */
function handleGraduateCertPrompt(message) {
  ElMessageBox.alert(
    message || '您尚未取得结业证书，暂不能进入学位申请。请先完成结业后再试。',
    '提示',
    {
      confirmButtonText: '知道了',
      type: 'warning',
    },
  ).catch(() => {})
}

/**
 * cookie操作封装
 */
const cookie = {
  getInClient(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return ''
  },
  setInClient({ key, val, expires }) {
    document.cookie = `${key}=${val}; expires=${expires.toUTCString()}; path=/`
  },
  delInClient(name) {
    const exp = new Date()
    exp.setTime(exp.getTime() - 1)
    const value = this.getInClient(name)
    if (value) {
      document.cookie = `${name}=${value}; expires=${exp.toUTCString()}; path=/`
    }
  }
}

/**
 * 存储相关操作
 */
const setSession = (key, val) => {
  sessionStorage.setItem(key, JSON.stringify(val))
}

const clearSession = () => {
  sessionStorage.clear()
}

/**
 * 创建HTTP请求实例
 */
const createHttp = () => {
  // 使用相对URL或基于当前窗口位置的URL
  let apiBaseUrl = '';


  if (import.meta.env.MODE === 'development') {
    // 本地
    if (typeof window === 'undefined') {
      apiBaseUrl = import.meta.env.VITE_API_BASE_URL || config.baseUrl;
    } else {
      // 在浏览器环境下，使用相对路径
      apiBaseUrl = '/gateway';
    }
  } else {
    // 测试线上环境
    if (import.meta.env.MODE === 'development') {
      // 开发环境使用配置的baseUrl
      apiBaseUrl = config.baseUrl;
    } else {
      // 生产环境使用相对路径，通过nginx代理转发
      apiBaseUrl = '/gateway';
    }
  }

  const options = {
    baseURL: apiBaseUrl,
    timeout: 60000,
    // 添加响应数据转换器，使用json-bigint解析
    transformResponse: [function (data) {
      // 处理空响应或非JSON格式数据
      if (!data) {
        return data;
      }
      // 如果是 Blob 或 ArrayBuffer，直接返回，不进行 JSON 解析
      if (data instanceof Blob || data instanceof ArrayBuffer) {
        return data;
      }
      // 如果是字符串，检查是否是 JSON 格式
      if (typeof data === 'string') {
        try {
          // 使用JSONparser解析数据（json-bigint实例）
          return JSONparser.parse(data);
        } catch (e) {
          // 解析失败时使用默认JSON解析
          console.warn('json-bigint解析失败，使用默认解析:', e);
          try {
            return JSON.parse(data);
          } catch (parseError) {
            // 如果也不是有效的 JSON，返回原始字符串
            return data;
          }
        }
      }
      // 其他类型直接返回
      return data;
    }]
  }
  const head = {
    deviceType: '6'
  }

  // 获取token和shopId
  const token = localStorage.getItem(config.tokenName) || cookie.getInClient(config.tokenName)

  // 根据域名判断shopId
  const getShopIdByDomain = () => {
    const hostname = window.location.hostname
    const domainShopIdMap = {
      'web.jxjy.ccucm.tdxl-edu.cn': '1971101586742583367',
      'web.jjy.hbucm.tdxl-edu.cn': '2089559997481512960',
      'tdxl.hbucm.edu.cn': '2089559997481512960',
      'web.cce.cqnu.tdxl-edu.cn': '1971019618323537972',
      'web.cj.cqut.tdxl-edu.cn': '1938118975376330752',
      'web-demo.ykumda.com': '1976489618094170159',
      'web.jjy.sntcm.tdxl-edu.cn': '1971101505972871261',
      'crm.shanxiyixue.com': '1976489618094170159',
      'ccrm.shanxiyixue.com': '1976489618094170159',
      'web.jlnu.tdxl-edu.cn': '1971101961574948951',
      'web.eed.swupl.tdxl-edu.cn': '1980112212546560078',
      'web.jxjyxy.hnust.tdxl-edu.cn': '1938118518574682156',
      'web.yjy.henu.tdxl-edu.cn': '2001095123080847360',
      'web.jxjy.blcu.tdxl-edu.cn': '2044607284197224448',
      'web.jxjy.wtu.tdxl-edu.cn': '2098223564984504320',
      'localhost': '1938118975376330752',
      '192.168.10.50': '1938118975376330752',
      '192.168.10.43': '1976489618094170159',
      // '192.168.10.174': '1978028179926822912',
      '192.168.10.174': '1976489618094170159',
    }

    // 优先从域名映射获取
    if (domainShopIdMap[hostname]) {
      return domainShopIdMap[hostname]
    }

    // 如果域名不在映射中，尝试从cookie获取
    // const cookieShopId = cookie.getInClient('shopId')
    // if (cookieShopId) {
    //   return cookieShopId
    // }

    // 默认返回原来的硬编码值
    return ''
  }

  const shopId = getShopIdByDomain()
  sessionStorage.setItem('SHOPID', shopId)
  console.log('API请求配置:', {
    baseURL: apiBaseUrl,
    currentHost: window.location.host,
    hostname: window.location.hostname,
    shopId: shopId
  })

  if (token) {
    head.token = token
  }

  if (shopId) {
    head.sid = shopId
  }

  options.headers = head
  let serverUrl = ''

  // 创建axios实例
  const http = axios.create(options)

  // 添加请求拦截器
  http.interceptors.request.use(
    function (config) {
      console.log('config', config)
      const token = localStorage.getItem('EDU_WEB_TOKEN')
      console.log('Token:', token)
      // if (token && config.headers) {
      //   // config.headers.Authorization = `Bearer ${token}`
      //   config.headers.Authorization = `${token}`
      // }
      if (token && config.headers && config.requireAuth === true) {
        config.headers.Authorization = `${token}`
      }
      serverUrl = config.url
      return config
    }, function (error) {
      // 对请求错误做些什么
      return Promise.reject(error)
    }
  )

  // 添加响应拦截器
  http.interceptors.response.use(function (response) {
    if (response.config.url.indexOf('/base/api/wx/op/bind/authorization/info') > -1) {
      return Promise.resolve(response.data)
    }

    // 请求登录接口完成后更新token过期时间
    if (serverUrl.indexOf('auth') != -1) {
      const NowTime = new Date()
      NowTime.setTime((NowTime.getTime()) + config.tokenExpires)
      cookie.setInClient({ key: config.tokenName, val: localStorage.getItem(config.tokenName), expires: NowTime })
    }

    // 优先处理 Blob 响应（文件下载）
    if (response.data instanceof Blob ||
        response.data instanceof ArrayBuffer ||
        response.responseType === 'blob' ||
        response.request.responseType === 'arraybuffer' ||
        response.config.responseType === 'blob') {
      return Promise.resolve(response.data)
    }

    // 跳过处理标记
    if (response.config.data || response.config.params) {
      let d = response.config.data || response.config.params
      if (typeof d === 'string') {
        try {
          d = JSON.parse(d)
        } catch (err) { }
      }
      if (d && d._No_dispose) {
        return Promise.resolve(response.data)
      }
    }

    // 处理特殊响应类型（文件下载等）
    if (response.headers["content-type"] == "application/octet-stream" ||
        response.headers["content-type"] == "application/pdf" ||
        response.headers["content-type"]?.includes('application/vnd.openxmlformats-officedocument') ||
        response.headers["content-type"]?.includes('application/msword')) {
      return Promise.resolve(response.data)
    }
    console.log(response.data,'response.data')


    // 特殊错误码：未缴费用提醒（优先于成功判断，避免被 code==200 短路）
    // 论文二阶段 1003 不写全局欠费标记，避免误拦课程
    if (response.data?.errorCode == '1003') {
      handleUnpaidFeePrompt(response.data.errorMessage, response.config?.url)
      return Promise.reject(response.data)
    }

    // 告知函未签：弹框「去签署」，勿用红色 Toast
    if (response.data?.errorCode == '1004') {
      handleNoticeSignPrompt(response.data.errorMessage)
      return Promise.reject(response.data)
    }

    // 未结业：弹框「知道了」，勿用红色 Toast
    if (response.data?.errorCode == '1005') {
      handleGraduateCertPrompt(response.data.errorMessage)
      return Promise.reject(response.data)
    }

    // 业务成功响应
    if (response.data && (response.data.success || response.data.code == 200)) {
      // 课程列表等闸门接口成功 = 一阶段已不欠费，清本地欠费标记/暂存弹窗
      if (isFeeGateSuccessUrl(response.config?.url)) {
        clearUnpaidFeeState()
      }
      return Promise.resolve(response.data.data || response.data)
    } else {
      ElMessage.error(response.data.errorMessage)
      // 处理token过期等情况
      if (response.data.errorCode=='UNAUTHORIZED_LOGIN') {
        // 客户端请求接口token过期让他重新登录
        setSession('userInfo', '')
        clearSession()
        cookie.delInClient(config.tokenName)
        localStorage.removeItem(config.tokenName)
        if (writePath.indexOf(window.location.pathname) === -1) {
          const _route = window.location.pathname + window.location.search
          window.location.href = '/login?redirect=' + encodeURIComponent(_route)
        }
        return Promise.reject(response.data)
      }

      // 处理错误信息显示
      const d = (response.config.data && JSON.parse(response.config.data)) || response.config.params
      if (!d || d.isShowErrTip !== false) {
        // 过滤同时多个接口报token错误会出现多个提示bug
        const title = localStorage.getItem('___errmsg')
        const time = localStorage.getItem('___errmsgTime')
        const newtime = (new Date()).getTime()
        if (title !== response.data.msg || (newtime - time) > 2000) {
          localStorage.setItem('___errmsg', response.data.msg)
          localStorage.setItem('___errmsgTime', newtime)
          // ElMessage.error(response.data.msg)
        }
        return Promise.reject(response.data.data || response.data)
      } else {
        return Promise.resolve(response.data.data || response.data)
      }
    }
  }, function (error) {
    console.log(error,'error')
    // 处理 HTTP 错误状态码
    if (error.response) {
      const status = error.response.status
      const errorData = error.response.data
      if (status === 401) {
        setSession('userInfo', '')
        clearSession()
        cookie.delInClient(config.tokenName)
        localStorage.removeItem(config.tokenName)
        if (writePath.indexOf(window.location.pathname) === -1) {
          const _route = window.location.pathname + window.location.search
          window.location.href = '/login?redirect=' + encodeURIComponent(_route)
        }
        return Promise.reject(errorData)
      } else if (errorData?.errorCode == '1003') {
        handleUnpaidFeePrompt(errorData?.errorMessage, error.config?.url)
        return Promise.reject(errorData || error)
      } else if (errorData?.errorCode == '1004') {
        handleNoticeSignPrompt(errorData?.errorMessage)
        return Promise.reject(errorData || error)
      } else if (errorData?.errorCode == '1005') {
        handleGraduateCertPrompt(errorData?.errorMessage)
        return Promise.reject(errorData || error)
      } else {
        // 显示错误信息，优先从 response.data 中获取
        const errorMessage = errorData?.errorMessage || errorData?.msg || errorData?.message || error.errorMessage || error.message || '请求失败，请稍后重试'
        ElMessage.error(errorMessage)
        return Promise.reject(errorData || error)
      }
    } else {
      // 网络错误或其他错误 - 也需检查1003错误码
      const errorData = error.response?.data || error.data || error
      if (errorData?.errorCode == '1003' || error.errorMessage == '该用户欠费') {
        handleUnpaidFeePrompt(errorData?.errorMessage || error.errorMessage, error.config?.url)
        return Promise.reject(error)
      }
      const errorMessage = error.errorMessage || error.message || '网络错误，请稍后重试'
      ElMessage.error(errorMessage)
      return Promise.reject(error)
    }
  })

  return http
}

export default createHttp

export function getMsg(data) {
  const http = createHttp()
  return http({
    url: '/base/api/shop/user/send/verification/code',
    method: 'post',
    data
  })
}
